import React from "react";
import {
  motion,
  useAnimation,
  useInView,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { useTranslation } from "react-i18next";

import "./LandingFooter.css";

const FOOTER_LINK_GROUPS = [
  {
    key: "explore",
    items: [
      {
        key: "services",
        href: "services",
      },
      {
        key: "contact",
        href: "contact",
      },
      {
        key: "faq",
        href: "faq",
      },
    ],
  },
  {
    key: "contact",
    items: [
      {
        key: "email",
        href: "mailto:support@renoassist.eu",
      },
    ],
  },
  {
    key: "follow",
    items: [
      {
        key: "linkedin",
        href: "https://www.linkedin.com/company/leg-up-project/posts/?feedView=all",
        external: true,
      },
    ],
  },
];

const LEGAL_LINKS = [
  {
    key: "privacy",
    href: "/privacy-policy",
  },
  {
    key: "terms",
    href: "/terms-and-conditions",
  },
  {
    key: "cookies",
    href: "#cookie-settings",
    cookieSettings: true,
  },
  // {
  //   key: "accessibility",
  //   href: "#accessibility",
  // },
];

export default function LandingFooter() {
  const { t } = useTranslation("home");
  const prefersReducedMotion = useReducedMotion();

  const footerRef = React.useRef(null);
  const controls = useAnimation();

  const openCookieSettings = (event) => {
    event.preventDefault();

    window.dispatchEvent(new Event("legup-open-cookie-settings"));
  };

  const isInView = useInView(footerRef, {
    amount: 0.01,
    margin: "0px 0px 5% 0px",
  });

  React.useEffect(() => {
    if (prefersReducedMotion) {
      controls.set("visible");
      return;
    }

    if (isInView) {
      controls.start("visible");
    } else {
      controls.set("hidden");
    }
  }, [controls, isInView, prefersReducedMotion]);

  const { scrollYProgress } = useScroll({
    target: footerRef,
    offset: ["start end", "end start"],
  });

  const smoothScrollProgress = useSpring(scrollYProgress, {
    stiffness: 90,
    damping: 28,
    mass: 0.35,
  });

  const backgroundY = useTransform(
    smoothScrollProgress,
    [0, 1],
    prefersReducedMotion ? [0, 0] : [-48, 48],
  );

  const heroY = useTransform(
    smoothScrollProgress,
    [0, 1],
    prefersReducedMotion ? [0, 0] : [22, -22],
  );

  const linksY = useTransform(
    smoothScrollProgress,
    [0, 1],
    prefersReducedMotion ? [0, 0] : [12, -12],
  );

  const bottomY = useTransform(
    smoothScrollProgress,
    [0, 1],
    prefersReducedMotion ? [0, 0] : [7, -7],
  );

  const container = {
    hidden: {},

    visible: {
      transition: {
        delayChildren: prefersReducedMotion ? 0 : 0.08,

        staggerChildren: prefersReducedMotion ? 0 : 0.09,
      },
    },
  };

  const item = {
    hidden: {
      opacity: 0,

      y: prefersReducedMotion ? 0 : 18,

      filter: prefersReducedMotion ? "blur(0px)" : "blur(5px)",
    },

    visible: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",

      transition: {
        duration: prefersReducedMotion ? 0.01 : 0.5,

        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  const titleLine = {
    hidden: {
      opacity: 0,

      y: prefersReducedMotion ? 0 : "65%",
    },

    visible: {
      opacity: 1,

      y: "0%",

      transition: {
        duration: prefersReducedMotion ? 0.01 : 0.48,

        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <motion.footer ref={footerRef} className="legup-footer">
      <motion.div
        className="legup-footer__parallax-glow"
        style={{
          y: backgroundY,
        }}
        aria-hidden="true"
      />

      <motion.div
        className="legup-footer__inner"
        initial="hidden"
        animate={controls}
        variants={{
          hidden: {
            opacity: prefersReducedMotion ? 1 : 0,

            y: prefersReducedMotion ? 0 : 24,
          },

          visible: {
            opacity: 1,

            y: 0,

            transition: {
              duration: prefersReducedMotion ? 0.01 : 0.58,

              ease: [0.22, 1, 0.36, 1],

              delayChildren: prefersReducedMotion ? 0 : 0.02,

              staggerChildren: prefersReducedMotion ? 0 : 0.045,
            },
          },
        }}
      >
        <div className="legup-footer__top">
          <motion.div
            className="legup-footer__hero-reveal"
            variants={container}
          >
            <motion.div
              className="legup-footer__hero"
              style={{
                y: heroY,
              }}
            >
              <motion.h2 className="legup-footer__title" variants={container}>
                <span className="legup-footer__title-mask">
                  <motion.span variants={titleLine}>
                    {t("footer.hero.line1")}
                  </motion.span>
                </span>

                <span className="legup-footer__title-mask">
                  <motion.span variants={titleLine}>
                    {t("footer.hero.line2")}
                  </motion.span>
                </span>

                <span className="legup-footer__title-mask">
                  <motion.span variants={titleLine}>
                    {t("footer.hero.line3")}
                  </motion.span>
                </span>

                <span className="legup-footer__title-mask">
                  <motion.span variants={titleLine}>
                    {t("footer.hero.line4")}
                  </motion.span>
                </span>
              </motion.h2>

              {/* <motion.a
                className="legup-footer__cta"
                href="#newsletter"
                variants={item}
                whileHover={
                  prefersReducedMotion
                    ? undefined
                    : {
                        y: -3,
                        scale: 1.012,
                      }
                }
                whileTap={
                  prefersReducedMotion
                    ? undefined
                    : {
                        scale: 0.985,
                      }
                }
              >
                <span>{t("footer.hero.subscribe")}</span>

                <motion.span
                  className="legup-footer__cta-arrow"
                  aria-hidden="true"
                  animate={
                    prefersReducedMotion
                      ? undefined
                      : {
                          x: [0, 4, 0],
                        }
                  }
                  transition={
                    prefersReducedMotion
                      ? undefined
                      : {
                          duration: 1.8,
                          repeat: Infinity,
                          repeatDelay: 1.2,
                          ease: "easeInOut",
                        }
                  }
                >
                  →
                </motion.span>
              </motion.a> */}
            </motion.div>
          </motion.div>

          {/* RIGHT / LINKS */}

          <motion.div
            className="legup-footer__links-reveal"
            variants={container}
          >
            <motion.div
              className="legup-footer__links"
              style={{
                y: linksY,
              }}
            >
              {FOOTER_LINK_GROUPS.map((group) => (
                <motion.div
                  key={group.key}
                  className="legup-footer__column"
                  variants={item}
                >
                  <h4>{t(`footer.groups.${group.key}.title`)}</h4>

                  {group.items.map((link) => (
                    <motion.a
                      key={link.key}
                      href={link.href}
                      target={link.external ? "_blank" : undefined}
                      rel={link.external ? "noreferrer" : undefined}
                      whileHover={
                        prefersReducedMotion
                          ? undefined
                          : {
                              x: 5,
                            }
                      }
                      transition={{
                        duration: 0.18,
                      }}
                    >
                      {t(`footer.groups.${group.key}.items.${link.key}`)}
                    </motion.a>
                  ))}
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>

        {/* DIVIDER */}

        <motion.div
          className="legup-footer__divider"
          variants={{
            hidden: {
              scaleX: 0,
              opacity: 0,
            },

            visible: {
              scaleX: 1,
              opacity: 1,

              transition: {
                duration: prefersReducedMotion ? 0.01 : 0.8,

                delay: prefersReducedMotion ? 0 : 0.18,

                ease: [0.22, 1, 0.36, 1],
              },
            },
          }}
        />

        {/* BOTTOM */}

        <motion.div
          className="legup-footer__bottom-reveal"
          variants={container}
        >
          <motion.div
            className="legup-footer__bottom"
            style={{
              y: bottomY,
            }}
          >
            <motion.div className="legup-footer__legal" variants={item}>
              {LEGAL_LINKS.map((link) => (
                <motion.a
                  key={link.key}
                  href={link.href}
                  onClick={link.cookieSettings ? openCookieSettings : undefined}
                  whileHover={
                    prefersReducedMotion
                      ? undefined
                      : {
                          y: -2,
                        }
                  }
                >
                  {t(`footer.legal.${link.key}`)}
                </motion.a>
              ))}
            </motion.div>

            <motion.div className="legup-footer__copyright" variants={item}>
              {t("footer.copyright")}
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>
    </motion.footer>
  );
}
